/**
 * Long work, shown without taking the window away.
 *
 * The face pass used a modal, on the reasoning that a silent background task
 * is one the user cannot tell has stalled. The first half of that is right and
 * the conclusion was not: the pass fetches 63 MB of model and then works
 * through the archive a photograph at a time, so it runs for minutes, and a
 * modal means minutes of not being able to name anybody, open a photograph or
 * look at an album — on the archive the pass is in the middle of improving.
 *
 * So: a ring in the corner. Always visible, so nothing is silent; never in
 * front of anything, so the archive stays usable. Click it for the detail and
 * the way to stop.
 *
 * It lives on `document.body` rather than in the header because `shell()`
 * replaces everything under the app root on every re-render — and the pass
 * causes re-renders, being the thing that changes what the views show. A ring
 * that vanished each time its own work updated the screen would be worse than
 * no ring. The header reserves room for it with a class on the root element
 * instead of holding it.
 */

import { h, svg as svgEl } from "./dom";

const R = 15;
const CIRCUMFERENCE = 2 * Math.PI * R;

export interface Working {
  /** Progress and a line of detail. `total` of 0 means "not countable yet". */
  update(line: string, done: number, total: number): void;
  /** Something went wrong; leave it on screen saying so. */
  fail(line: string): void;
  /** Finished. Shows the ring full for a moment, then takes it away. */
  close(): void;
}

export function working(title: string, onStop?: () => void): Working {
  const arc = svgEl("circle", {
    class: "ring-arc",
    cx: "18", cy: "18", r: String(R),
    "stroke-dasharray": String(CIRCUMFERENCE),
    "stroke-dashoffset": String(CIRCUMFERENCE),
  });
  const pct = h("span", { class: "ring-pct", text: "" });
  const ring = svgEl(
    "svg",
    { class: "ring-svg", viewBox: "0 0 36 36", "aria-hidden": "true" },
    svgEl("circle", { class: "ring-track", cx: "18", cy: "18", r: String(R) }),
    arc,
  );

  const knob = h(
    "button",
    {
      class: "ring",
      // The accessible name carries the state, because the ring is a picture
      // and the percentage inside it is three characters of decoration to a
      // screen reader.
      "aria-label": `${title} — starting`,
      "aria-live": "polite",
    },
    ring,
    pct,
  );

  const say = h("p", { class: "ring-say", text: "Starting…" });
  const stopBtn = onStop
    ? h(
        "button",
        {
          class: "btn ghost sm",
          onclick: () => {
            say.textContent = "stopping after this one…";
            onStop();
          },
        },
        "Stop",
      )
    : null;
  const panel = h(
    "div",
    { class: "ring-panel", hidden: true },
    h("h3", { class: "ring-title", text: title }),
    say,
    ...(stopBtn ? [stopBtn] : []),
  );

  const host = h("div", { class: "working" }, knob, panel);

  let open = false;
  const setOpen = (v: boolean): void => {
    open = v;
    panel.hidden = !open;
    knob.classList.toggle("on", open);
  };
  knob.onclick = () => setOpen(!open);

  // Clicking elsewhere closes the detail. Not Escape-only: the panel is a
  // disclosure, not a dialog, and nothing in it traps focus.
  const away = (e: MouseEvent): void => {
    if (open && !host.contains(e.target as Node)) setOpen(false);
  };
  document.addEventListener("mousedown", away);

  document.body.appendChild(host);
  document.documentElement.classList.add("has-working");

  let done = false;

  const setFraction = (f: number | null): void => {
    if (f === null) {
      // Nothing countable yet — loading a model, or waiting on a list. A ring
      // stuck at zero reads as stalled, so it sweeps instead.
      host.classList.add("indeterminate");
      arc.setAttribute("stroke-dashoffset", String(CIRCUMFERENCE * 0.75));
      pct.textContent = "";
      return;
    }
    host.classList.remove("indeterminate");
    const clamped = Math.max(0, Math.min(1, f));
    arc.setAttribute("stroke-dashoffset", String(CIRCUMFERENCE * (1 - clamped)));
    pct.textContent = `${Math.round(clamped * 100)}`;
  };

  return {
    update(line, doneCount, total) {
      if (done) return;
      say.textContent = line;
      setFraction(total > 0 ? doneCount / total : null);
      knob.setAttribute(
        "aria-label",
        total > 0 ? `${title} — ${doneCount} of ${total}` : `${title} — ${line}`,
      );
    },

    fail(line) {
      if (done) return;
      done = true;
      host.classList.remove("indeterminate");
      host.classList.add("failed");
      say.textContent = line;
      knob.setAttribute("aria-label", `${title} — ${line}`);
      // Left on screen and opened, because a failure nobody sees is the thing
      // this whole module exists to avoid. Clicking the ring dismisses it.
      setOpen(true);
      knob.onclick = () => {
        document.removeEventListener("mousedown", away);
        host.remove();
        document.documentElement.classList.remove("has-working");
      };
    },

    close() {
      if (done) return;
      done = true;
      host.classList.remove("indeterminate");
      host.classList.add("finished");
      setFraction(1);
      setOpen(false);
      // A beat at full, so a pass that finishes quickly still registers as
      // having happened rather than as a flicker.
      window.setTimeout(() => {
        document.removeEventListener("mousedown", away);
        host.remove();
        document.documentElement.classList.remove("has-working");
      }, 900);
    },
  };
}
